/**
 * @fileoverview Centralized strategy registry and configuration
 * 
 * This module provides a single source of truth for all strategy-related configurations,
 * eliminating the need to maintain strategy mappings in multiple places.
 */

export interface StrategyParameter {
  name: string;
  type: 'number' | 'string' | 'boolean';
  label: string;
  description?: string;
  defaultValue: number | string | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: string[]; // For select/enum parameters
  category?: string;
}

export interface ParameterRange {
  min: number;
  max: number;
  step: number;
  current: [number, number];
  fixed?: number;
}

export interface StrategyDefinition {
  // Core identification
  id: string;
  slug: string;
  name: string;
  description: string;
  
  // Categorization
  category: string;
  tags: string[];
  
  // Access control
  tier: 'independent' | 'professional' | 'advanced';
  isPublic: boolean;
  
  // Parameters
  parameters: StrategyParameter[];
  
  // UI Configuration
  defaultParameterRanges: { [key: string]: ParameterRange };
  
  // Legacy aliases for backwards compatibility
  aliases: string[];
  
  // Metadata
  supportedAssets: string[];
  supportedTimeframes: string[];
  
  // Runtime
  isBuiltIn: boolean;
  isTemplate: boolean;
  executionType: 'code' | 'visual';
  codeHandler?: string;
}

/**
 * Centralized strategy registry
 * Note: All built-in strategies have been migrated to QSL templates.
 * This registry is kept for backward compatibility but defines no built-in strategies.
 * Strategies are loaded from StrategyProject documents in the database.
 */
export const STRATEGY_REGISTRY: { [key: string]: StrategyDefinition } = {
  // All strategies are now QSL-based (StrategyProject in database)
};

/**
 * Strategy utilities
 */
export class StrategyRegistry {
  /**
   * Get strategy definition by ID or alias
   */
  static getStrategy(identifier: string): StrategyDefinition | null {
    // Direct ID lookup
    if (STRATEGY_REGISTRY[identifier]) {
      return STRATEGY_REGISTRY[identifier];
    }
    
    // Alias lookup
    for (const strategy of Object.values(STRATEGY_REGISTRY)) {
      if (strategy.aliases.includes(identifier)) {
        return strategy;
      }
    }
    
    return null;
  }
  
  /**
   * Get all strategy IDs
   */
  static getAllStrategyIds(): string[] {
    return Object.keys(STRATEGY_REGISTRY);
  }
  
  /**
   * Get all strategies as array
   */
  static getAllStrategies(): StrategyDefinition[] {
    return Object.values(STRATEGY_REGISTRY);
  }
  
  /**
   * Get strategies by tier
   */
  static getStrategiesByTier(userTier: string): StrategyDefinition[] {
    const tierHierarchy = ['independent', 'professional', 'advanced'];
    const userTierIndex = tierHierarchy.indexOf(userTier);
    const accessibleTiers = userTierIndex >= 0
      ? tierHierarchy.slice(0, userTierIndex + 1)
      : [];
    
    return Object.values(STRATEGY_REGISTRY).filter(
      strategy => accessibleTiers.includes(strategy.tier)
    );
  }
  
  /**
   * Get parameter ranges for strategy
   */
  static getParameterRanges(strategyId: string): { [key: string]: ParameterRange } {
    const strategy = this.getStrategy(strategyId);
    return strategy?.defaultParameterRanges || {};
  }
  
  /**
   * Get parameter labels for strategy
   */
  static getParameterLabels(strategyId: string): { [key: string]: string } {
    const strategy = this.getStrategy(strategyId);
    if (!strategy) return {};
    
    const labels: { [key: string]: string } = {};
    strategy.parameters.forEach(param => {
      labels[param.name] = param.label;
    });
    return labels;
  }
  
  /**
   * Normalize strategy identifier to canonical ID
   */
  static normalizeStrategyId(identifier: string): string | null {
    const strategy = this.getStrategy(identifier);
    return strategy?.id || null;
  }
}
